import { cn } from '@/lib/cn'

/*
 * 点阵小图标。每个字形是 6×6 的格子，`#` 是实心格。
 * 默认 12px 显示（2× 整数倍），边缘是硬的；要放大就取 6 的整数倍。
 *
 * 只给「身份」这类标识用 —— 导航、操作栏仍用 lucide：24px 的点阵图标
 * 在常用操作上可读性不如线条图标，像素母题留给需要一眼认出来的地方。
 */
export const PIXEL_GLYPHS = {
  // 机器人头：天线、方脸、两只眼、一张嘴
  agent: ['..##..', '######', '#.##.#', '######', '##..##', '######'],
  // 人
  human: ['..##..', '.####.', '.####.', '..##..', '.####.', '######'],
  // 对勾：签名可独立复核
  check: ['.....#', '....##', '#..##.', '####..', '.##...', '......'],
} as const

export type PixelGlyph = keyof typeof PIXEL_GLYPHS

// 同一行里连续的 # 合成一个 rect，DOM 里少几十个节点
function runs(rows: readonly string[]) {
  const rects: { x: number; y: number; w: number }[] = []
  rows.forEach((row, y) => {
    let x = 0
    while (x < row.length) {
      if (row[x] !== '#') {
        x += 1
        continue
      }
      const start = x
      while (x < row.length && row[x] === '#') x += 1
      rects.push({ x: start, y, w: x - start })
    }
  })
  return rects
}

const RECTS = Object.fromEntries(
  Object.entries(PIXEL_GLYPHS).map(([name, rows]) => [name, runs(rows)]),
) as Record<PixelGlyph, ReturnType<typeof runs>>

export function PixelIcon({ glyph, className }: { glyph: PixelGlyph; className?: string }) {
  return (
    <svg
      viewBox="0 0 6 6"
      shapeRendering="crispEdges"
      fill="currentColor"
      aria-hidden
      className={cn('size-3 shrink-0', className)}
    >
      {RECTS[glyph].map(({ x, y, w }) => (
        <rect key={`${x}-${y}`} x={x} y={y} width={w} height={1} />
      ))}
    </svg>
  )
}
